import React, { useState, useEffect } from 'react';
import DeviceDetail from './DeviceDetail';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import BulkImport from './BulkImport';

const Devices = ({ primary }) => {
  const { user } = useAuth();
  const [devices, setDevices] = useState([]);
  const [acsDevices, setAcsDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [tab, setTab] = useState('registered');
  const [showAdd, setShowAdd] = useState(false);
  const [registerModal, setRegisterModal] = useState(null);
  const [registerForm, setRegisterForm] = useState({ customer_name: '', customer_phone: '', customer_id: '', area: '', sub_dist_id: '', lco_id: '' });
  const [registerLoading, setRegisterLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterTenant, setFilterTenant] = useState('');
  const [page, setPage] = useState(1);
  const [tenants, setTenants] = useState([]);
  const [actionDevice, setActionDevice] = useState(null);
  const [editDevice, setEditDevice] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [firmwareUrl, setFirmwareUrl] = useState('');
  const [wanConfig, setWanConfig] = useState({ pppoe_user: '', pppoe_pass: '', vlan: '' });
  const PER_PAGE = 10;
  const [form, setForm] = useState({ serial_number: '', genieacs_id: '', gpon_serial: '', customer_name: '', customer_phone: '', customer_id: '', area: '', vendor: '', model: '', sub_dist_id: '', lco_id: '' });
  const [subdists, setSubdists] = useState([]);
  const [lcos, setLcos] = useState([]);
  const [filteredLcos, setFilteredLcos] = useState([]);

  useEffect(() => { loadData(); loadSubdists(); }, []);

  const loadSubdists = async () => {
    try {
      const [subRes, lcoRes] = await Promise.all([
        axios.get('/api/users?role=sub_dist_admin'),
        axios.get('/api/users?role=lco_admin')
      ]);
      setSubdists(subRes.data.users || []);
      setLcos(lcoRes.data.users || []);
    } catch(e) { console.error(e); }
  };

  const loadData = async () => {
    try {
      const promises = [axios.get('/api/devices'), axios.get('/api/acs/devices')];
      if (user.role === 'superadmin') promises.push(axios.get('/api/tenants'));
      const results = await Promise.all(promises);
      setDevices(results[0].data.devices || []);
      setAcsDevices(results[1].data.devices || []);
      if (user.role === 'superadmin' && results[2]) setTenants(results[2].data.tenants || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const showMsg = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg({ text: '', type: '' }), 4000); };

  const handleAdd = async () => {
    try {
      await axios.post('/api/devices', form);
      showMsg('Device registered successfully!');
      setShowAdd(false);
      setForm({ serial_number: '', genieacs_id: '', gpon_serial: '', customer_name: '', customer_phone: '', customer_id: '', area: '', vendor: '', model: '' });
      loadData();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
  };

  const handleDeleteAcs = async (acsId) => {
    if (!window.confirm('Remove this unregistered device from ACS?')) return;
    try {
      await axios.delete('/api/acs/devices/' + encodeURIComponent(acsId));
      showMsg('Device removed from ACS!');
      loadData();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
  };

  const handleRegisterModal = (acsDevice) => {
    setRegisterModal(acsDevice);
    setRegisterForm({ customer_name: '', customer_phone: '', customer_id: '', area: '', sub_dist_id: '', lco_id: '' });
  };

  const handleRegisterSubmit = async () => {
    if (!registerForm.customer_name || !registerForm.customer_phone) {
      showMsg('Customer name and phone are required', 'error'); return;
    }
    setRegisterLoading(true);
    try {
      await axios.post('/api/devices', {
        serial_number: registerModal._deviceId._SerialNumber,
        genieacs_id: registerModal._id,
        vendor: registerModal._deviceId._Manufacturer || '',
        model: registerModal._deviceId._ProductClass || '',
        ...registerForm
      });
      showMsg('Device registered successfully! WiFi configured.');
      setRegisterModal(null);
      loadData();
    } catch(e) { showMsg(e.response?.data?.message || 'Registration failed', 'error'); }
    finally { setRegisterLoading(false); }
  };

  const handleQuickRegister = (acsDevice) => {
    setForm({ serial_number: acsDevice._deviceId._SerialNumber, genieacs_id: acsDevice._id, vendor: acsDevice._deviceId._Manufacturer || '', model: acsDevice._deviceId._ProductClass || '', customer_name: '', customer_phone: '', customer_id: '', area: '' });
    setShowAdd(true); setTab('registered');
  };

  const handleReboot = async (id) => {
    if (!window.confirm('Reboot this device?')) return;
    try { await axios.post('/api/devices/' + id + '/reboot'); showMsg('Reboot command sent!'); }
    catch (e) { showMsg('Failed to reboot', 'error'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm('Delete device for ' + (name || 'this customer') + '? This cannot be undone!')) return;
    try {
      await axios.delete('/api/devices/' + id);
      showMsg('Device deleted successfully!');
      loadData();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed to delete', 'error'); }
  };

  const handleEdit = async () => {
    try {
      await axios.put('/api/devices/' + editDevice._id, editForm);
      showMsg('Device updated successfully!');
      setEditDevice(null);
      loadData();
    } catch (e) { showMsg(e.response?.data?.message || 'Failed to update', 'error'); }
  };

  const handleFirmwareUpgrade = async (deviceId) => {
    if (!firmwareUrl) { showMsg('Please enter firmware URL', 'error'); return; }
    try {
      await axios.post('/api/devices/' + deviceId + '/firmware', { url: firmwareUrl });
      showMsg('Firmware upgrade initiated!');
      setActionDevice(null); setFirmwareUrl('');
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
  };

  const handleWanConfig = async (deviceId) => {
    try {
      await axios.post('/api/devices/' + deviceId + '/wan-config', wanConfig);
      showMsg('WAN configuration updated!');
      setActionDevice(null);
    } catch (e) { showMsg(e.response?.data?.message || 'Failed', 'error'); }
  };

  const filteredDevices = devices.filter(d => {
    const s = search.toLowerCase();
    const matchSearch = !s || (d.customer_name && d.customer_name.toLowerCase().includes(s)) || (d.customer_phone && d.customer_phone.includes(s)) || (d.serial_number && d.serial_number.toLowerCase().includes(s)) || (d.gpon_serial && d.gpon_serial.toLowerCase().includes(s)) || (d.area && d.area.toLowerCase().includes(s));
    const matchStatus = !filterStatus || d.status === filterStatus;
    const matchTenant = !filterTenant || (d.tenant_id && (d.tenant_id.code === filterTenant || d.tenant_id === filterTenant));
    return matchSearch && matchStatus && matchTenant;
  });

  const totalPages = Math.ceil(filteredDevices.length / PER_PAGE);
  const paginatedDevices = filteredDevices.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const registeredSerials = devices.map(d => d.serial_number);
  const unregistered = acsDevices.filter(d => d._deviceId && d._deviceId._SerialNumber && d._deviceId._Manufacturer !== 'DISCOVERYSERVICE' && !registeredSerials.includes(d._deviceId._SerialNumber));
  const online = devices.filter(d => d.status === 'online').length;
  const offline = devices.filter(d => d.status === 'offline').length;

  if (selectedDevice) return <DeviceDetail deviceId={selectedDevice} onBack={() => setSelectedDevice(null)} primary={primary} />;

  return (
    <div>
      <style>{`
        .action-btn { transition: all 0.15s; }
        .action-btn:hover { opacity: 0.85; transform: translateY(-1px); }
        .device-row:hover { background: #f8fafc !important; }
        @keyframes slideIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px', fontSize: '24px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.5px' }}>Device Management</h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Monitor and manage ONT devices across your network</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setShowBulk(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '700', color: '#475569' }}>
            📥 Bulk Import
          </button>
          <button onClick={() => setShowAdd(!showAdd)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: showAdd ? '#ef4444' : primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '700' }}>
            {showAdd ? '✕ Cancel' : '+ Register Device'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        {[
          { label: 'Total Devices', value: devices.length, color: primary, bg: `${primary}10`, icon: '📡', sub: 'Registered ONTs' },
          { label: 'Online', value: online, color: '#10b981', bg: '#ecfdf5', icon: '🟢', sub: 'Connected now' },
          { label: 'Offline', value: offline, color: '#ef4444', bg: '#fef2f2', icon: '🔴', sub: 'Not responding' },
          { label: 'Pending', value: unregistered.length, color: '#f59e0b', bg: '#fffbeb', icon: '⏳', sub: 'Need registration' }
        ].map(s => (
          <div key={s.label} style={{ background: 'white', borderRadius: '16px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, right: 0, width: '64px', height: '64px', background: s.bg, borderRadius: '0 16px 0 64px', display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', padding: '10px' }}>
              <span style={{ fontSize: '22px' }}>{s.icon}</span>
            </div>
            <p style={{ margin: '0 0 6px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>{s.label}</p>
            <h2 style={{ margin: '0 0 4px', fontSize: '32px', fontWeight: '800', color: s.color, lineHeight: 1 }}>{s.value}</h2>
            <p style={{ margin: 0, fontSize: '12px', color: '#94a3b8' }}>{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Message */}
      {msg.text && (
        <div style={{ background: msg.type === 'error' ? '#fef2f2' : '#ecfdf5', color: msg.type === 'error' ? '#dc2626' : '#16a34a', padding: '12px 16px', borderRadius: '10px', marginBottom: '16px', fontSize: '14px', fontWeight: '500', border: `1px solid ${msg.type === 'error' ? '#fecaca' : '#bbf7d0'}`, animation: 'slideIn 0.2s ease' }}>
          {msg.type === 'error' ? '⚠️' : '✅'} {msg.text}
        </div>
      )}

      {/* Action Modal - Firmware / WAN Config */}
      {actionDevice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '480px', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', animation: 'slideIn 0.2s ease' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                {actionDevice.mode === 'firmware' ? '⬆️ Firmware Upgrade' : '🌐 WAN Configuration'}
              </h3>
              <button onClick={() => setActionDevice(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', fontSize: '16px', color: '#64748b' }}>✕</button>
            </div>

            <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', fontSize: '13px', color: '#475569' }}>
              <strong>Device:</strong> {actionDevice.serial} | <strong>Vendor:</strong> {actionDevice.vendor}
            </div>

            {actionDevice.mode === 'firmware' ? (
              <div>
                <div style={{ background: '#f5f3ff', border: '1px solid #ede9fe', borderRadius: '10px', padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#5b21b6' }}>
                  ⚠️ Device will reboot after firmware upgrade. Ensure firmware is compatible with <strong>{actionDevice.vendor} {actionDevice.model}</strong>.
                </div>

                <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '8px' }}>Option 1: Upload from PC</label>
                <div style={{ border: '2px dashed #e2e8f0', borderRadius: '10px', padding: '20px', textAlign: 'center', marginBottom: '16px', cursor: 'pointer', transition: 'all 0.2s' }}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#8b5cf6'; }}
                  onDragLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                  onClick={() => document.getElementById('fw-upload').click()}>
                  <input id="fw-upload" type="file" accept=".bin,.img,.tar,.gz,.zip" style={{ display: 'none' }}
                    onChange={e => {
                      const file = e.target.files[0];
                      if (file) setFirmwareUrl('file:' + file.name + '|' + URL.createObjectURL(file));
                    }} />
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📁</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#374151' }}>Click or drag firmware file here</div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>Supported: .bin, .img, .tar.gz, .zip</div>
                  {firmwareUrl.startsWith('file:') && (
                    <div style={{ marginTop: '10px', padding: '8px', background: '#ecfdf5', borderRadius: '8px', fontSize: '13px', color: '#16a34a', fontWeight: '600' }}>
                      ✅ {firmwareUrl.split('|')[0].replace('file:','')}
                    </div>
                  )}
                </div>

                <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '8px' }}>Option 2: Enter URL (ONT downloads directly)</label>
                <input value={firmwareUrl.startsWith('file:') ? '' : firmwareUrl}
                  onChange={e => setFirmwareUrl(e.target.value)}
                  placeholder="http://your-server.com/firmware.bin"
                  style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', marginBottom: '16px', boxSizing: 'border-box' }} />

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => handleFirmwareUpgrade(actionDevice.id)}
                    disabled={!firmwareUrl}
                    style={{ flex: 1, padding: '12px', background: firmwareUrl ? '#8b5cf6' : '#e2e8f0', color: firmwareUrl ? 'white' : '#94a3b8', border: 'none', borderRadius: '10px', cursor: firmwareUrl ? 'pointer' : 'not-allowed', fontWeight: '700', fontSize: '14px' }}>
                    ⬆️ Start Firmware Upgrade
                  </button>
                  <button onClick={() => setActionDevice(null)}
                    style={{ padding: '12px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div>
                {[
                  { key: 'pppoe_user', label: 'PPPoE Username', placeholder: 'e.g. network1234@isp' },
                  { key: 'pppoe_pass', label: 'PPPoE Password', placeholder: 'PPPoE password', type: 'password' },
                  { key: 'vlan', label: 'VLAN ID (optional)', placeholder: 'e.g. 100' }
                ].map(f => (
                  <div key={f.key} style={{ marginBottom: '14px' }}>
                    <label style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>{f.label}</label>
                    <input type={f.type || 'text'} value={wanConfig[f.key]} onChange={e => setWanConfig({ ...wanConfig, [f.key]: e.target.value })}
                      placeholder={f.placeholder}
                      style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }} />
                  </div>
                ))}
                <p style={{ fontSize: '12px', color: '#94a3b8', margin: '0 0 20px' }}>⚠️ WAN changes will take effect on next ONT inform cycle.</p>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={() => handleWanConfig(actionDevice.id)}
                    style={{ flex: 1, padding: '12px', background: primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}>
                    💾 Apply WAN Config
                  </button>
                  <button onClick={() => setActionDevice(null)}
                    style={{ padding: '12px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showBulk && <BulkImport primary={primary} onClose={() => { setShowBulk(false); loadData(); }} />}

      {/* Edit Device Modal */}
      {editDevice && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '520px', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', animation: 'slideIn 0.2s ease' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>✏️ Edit Customer Details</h3>
              <button onClick={() => setEditDevice(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', fontSize: '16px', color: '#64748b' }}>✕</button>
            </div>
            <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '10px 14px', marginBottom: '18px', fontSize: '13px', color: '#475569' }}>
              📡 Serial: <strong>{editDevice.serial_number}</strong>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                { key: 'customer_name', label: 'Customer Name', placeholder: 'Full name' },
                { key: 'customer_phone', label: 'Phone Number', placeholder: '10-digit mobile' },
                { key: 'customer_id', label: 'Customer/Connection ID', placeholder: 'Billing ID' },
                { key: 'area', label: 'Area/Locality', placeholder: 'e.g. Payyanur' },
                { key: 'vendor', label: 'Vendor', placeholder: 'e.g. XPON' },
                { key: 'model', label: 'Model', placeholder: 'e.g. ONT2GE1P2WR' }
              ].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '5px' }}>{f.label}</label>
                  <input
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', boxSizing: 'border-box' }}
                    value={editForm[f.key] || ''}
                    onChange={e => setEditForm({ ...editForm, [f.key]: e.target.value })}
                    placeholder={f.placeholder} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={handleEdit}
                style={{ flex: 1, padding: '12px', background: primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px' }}>
                💾 Save Changes
              </button>
              <button onClick={() => setEditDevice(null)}
                style={{ padding: '12px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Register Form */}
      {showAdd && (
        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', marginBottom: '20px', animation: 'slideIn 0.2s ease' }}>
          <h3 style={{ margin: '0 0 20px', fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>📡 Register New Device</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
            {[
              { key: 'serial_number', label: 'Serial Number *', placeholder: 'e.g. HWTC5E23449C' },
              { key: 'genieacs_id', label: 'ACS Device ID *', placeholder: 'e.g. 14C35E-ONT2GE1P2WR-...' },
              { key: 'customer_name', label: 'Customer Name', placeholder: 'Full name' },
              { key: 'customer_phone', label: 'Customer Phone *', placeholder: '10-digit mobile' },
              { key: 'customer_id', label: 'Customer/Connection ID', placeholder: 'Billing ID' },
              { key: 'area', label: 'Area/Locality', placeholder: 'e.g. Payyanur' },
              { key: 'gpon_serial', label: 'GPON/PON Serial (on device label)', placeholder: 'e.g. HWTC1CB65174' },
              { key: 'vendor', label: 'Vendor', placeholder: 'e.g. XPON, Realtek' },
              { key: 'model', label: 'Model', placeholder: 'e.g. HGX-323DAC-BC' }
            ].map(f => (
              <div key={f.key}>
                <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>{f.label}</label>
                <input style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box', transition: 'border 0.15s', outline: 'none' }}
                  value={form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.value })} placeholder={f.placeholder}
                  onFocus={e => e.target.style.borderColor = primary}
                  onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
              </div>
            ))}
            {subdists.length > 0 && (
              <div>
                <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>Sub-Distribution</label>
                <select style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                  value={form.sub_dist_id} onChange={e => {
                    const sid = e.target.value;
                    setForm({ ...form, sub_dist_id: sid, lco_id: '' });
                    setFilteredLcos(sid ? lcos.filter(l => l.sub_dist_id === sid) : lcos);
                  }}>
                  <option value=''>-- Select Sub-Dist (optional) --</option>
                  {subdists.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
              </div>
            )}
            {lcos.length > 0 && (
              <div>
                <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>LCO</label>
                <select style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                  value={form.lco_id} onChange={e => setForm({ ...form, lco_id: e.target.value })}>
                  <option value=''>-- Select LCO (optional) --</option>
                  {(filteredLcos.length > 0 ? filteredLcos : lcos).map(l => <option key={l._id} value={l._id}>{l.name} ({l.lco_id || l.email})</option>)}
                </select>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
            <button onClick={handleAdd} style={{ padding: '11px 24px', background: primary, color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '700', fontSize: '14px', boxShadow: `0 4px 12px ${primary}40` }}>
              💾 Save Device
            </button>
            <button onClick={() => setShowAdd(false)} style={{ padding: '11px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '2px solid #f1f5f9', paddingBottom: '0' }}>
        {[
          { id: 'registered', label: `📡 Registered (${devices.length})`, active: tab === 'registered', color: primary },
          { id: 'unregistered', label: `⏳ Pending Registration (${unregistered.length})`, active: tab === 'unregistered', color: '#f59e0b' }
        ].map(t => (
          <button key={t.id} onClick={() => { setTab(t.id); setPage(1); }} style={{
            padding: '10px 20px', background: 'transparent', border: 'none',
            borderBottom: t.active ? `3px solid ${t.color}` : '3px solid transparent',
            color: t.active ? t.color : '#64748b', fontWeight: t.active ? '700' : '500',
            fontSize: '14px', cursor: 'pointer', transition: 'all 0.15s', marginBottom: '-2px'
          }}>{t.label}</button>
        ))}
      </div>

      {/* Registered Devices */}
      {tab === 'registered' && (
        <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
          {/* Search & Filter */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
              <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '15px' }}>🔍</span>
              <input placeholder="Search by name, phone, serial, area..." value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                style={{ width: '100%', padding: '10px 14px 10px 36px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
                onFocus={e => e.target.style.borderColor = primary}
                onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
            </div>
            <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
              style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', color: '#374151', outline: 'none', cursor: 'pointer' }}>
              <option value="">All Status</option>
              <option value="online">🟢 Online</option>
              <option value="offline">🔴 Offline</option>
              <option value="unknown">⚪ Unknown</option>
            </select>
            {user.role === 'superadmin' && (
              <select value={filterTenant} onChange={e => { setFilterTenant(e.target.value); setPage(1); }}
                style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', color: '#374151', outline: 'none', cursor: 'pointer' }}>
                <option value="">All Tenants</option>
                {tenants.filter(t => t.type !== 'platform').map(t => <option key={t._id} value={t.code}>{t.name}</option>)}
              </select>
            )}
            {(search || filterStatus || filterTenant) && (
              <button onClick={() => { setSearch(''); setFilterStatus(''); setFilterTenant(''); setPage(1); }}
                style={{ padding: '10px 14px', background: '#f1f5f9', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                ✕ Clear
              </button>
            )}
            <span style={{ fontSize: '13px', color: '#94a3b8', marginLeft: 'auto' }}>
              {filteredDevices.length} devices
            </span>
          </div>

          {loading ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>📡</div>Loading devices...
            </div>
          ) : filteredDevices.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🔍</div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>No devices found</div>
              <div style={{ fontSize: '14px' }}>Try adjusting your search or filters</div>
            </div>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                    {[
                      ...(user.role === 'superadmin' ? ['Tenant'] : []),
                      'Customer', 'Phone', 'Serial Number', 'Device', 'Area', 'Status', 'Actions'
                    ].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedDevices.map((d) => (
                    <tr key={d._id} className="device-row" style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.15s', cursor: 'pointer' }}>
                      {user.role === 'superadmin' && (
                        <td style={{ padding: '14px 16px' }}>
                          <span style={{ background: `${primary}15`, color: primary, padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '700' }}>
                            {d.tenant_id ? (typeof d.tenant_id === 'object' ? d.tenant_id.code : 'N/A') : 'N/A'}
                          </span>
                        </td>
                      )}
                      <td style={{ padding: '14px 16px' }} onClick={() => setSelectedDevice(d._id)}>
                        <div style={{ fontWeight: '700', fontSize: '14px', color: '#1e293b' }}>{d.customer_name || 'N/A'}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{d.customer_id || 'No ID'}</div>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#374151' }} onClick={() => setSelectedDevice(d._id)}>{d.customer_phone || 'N/A'}</td>
                      <td style={{ padding: '14px 16px' }} onClick={() => setSelectedDevice(d._id)}>
  <div>
                        <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#475569', background: '#f1f5f9', padding: '3px 8px', borderRadius: '6px' }}>
                          {d.gpon_serial || d.serial_number}
                        </span>
                        {d.gpon_serial && d.gpon_serial !== d.serial_number && (
                          <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>TR-069: {d.serial_number}</div>
                        )}
                      </div>
                      </td>
                      <td style={{ padding: '14px 16px' }} onClick={() => setSelectedDevice(d._id)}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{d.vendor}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>{d.model}</div>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#374151' }} onClick={() => setSelectedDevice(d._id)}>{d.area || '—'}</td>
                      <td style={{ padding: '14px 16px' }} onClick={() => setSelectedDevice(d._id)}>
                        <span style={{
                          background: d.status === 'online' ? '#ecfdf5' : d.status === 'offline' ? '#fef2f2' : '#f8fafc',
                          color: d.status === 'online' ? '#16a34a' : d.status === 'offline' ? '#dc2626' : '#94a3b8',
                          padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700',
                          display: 'inline-flex', alignItems: 'center', gap: '4px'
                        }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
                          {d.status === 'online' ? 'Online' : d.status === 'offline' ? 'Offline' : 'Unknown'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button className="action-btn" onClick={() => setSelectedDevice(d._id)}
                            title="View Details"
                            style={{ padding: '6px 10px', background: `${primary}15`, color: primary, border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                            👁️ View
                          </button>
                          <button className="action-btn" onClick={() => handleReboot(d._id)}
                            title="Reboot Device"
                            style={{ padding: '6px 10px', background: '#fff7ed', color: '#ea580c', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                            🔄
                          </button>
                          <button className="action-btn" onClick={() => handleDelete(d._id, d.customer_name)}
                            title="Delete Device"
                            style={{ padding: '6px 10px', background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={{ padding: '16px 20px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>
                    Showing {((page-1)*PER_PAGE)+1}–{Math.min(page*PER_PAGE, filteredDevices.length)} of {filteredDevices.length}
                  </span>
                  <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                    {[['«', 1], ['‹', page-1]].map(([label, p]) => (
                      <button key={label} onClick={() => setPage(Math.max(1,p))} disabled={page===1}
                        style={{ padding: '6px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: page===1?'not-allowed':'pointer', color: '#475569', opacity: page===1?0.5:1 }}>{label}</button>
                    ))}
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      const p = Math.max(1, Math.min(page-2, totalPages-4)) + i;
                      return p <= totalPages ? (
                        <button key={p} onClick={() => setPage(p)}
                          style={{ padding: '6px 12px', background: p===page ? primary : '#f8fafc', border: `1px solid ${p===page ? primary : '#e2e8f0'}`, borderRadius: '8px', cursor: 'pointer', color: p===page?'white':'#475569', fontWeight: p===page?'700':'400' }}>{p}</button>
                      ) : null;
                    })}
                    {[['›', page+1], ['»', totalPages]].map(([label, p]) => (
                      <button key={label} onClick={() => setPage(Math.min(totalPages,p))} disabled={page===totalPages}
                        style={{ padding: '6px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', cursor: page===totalPages?'not-allowed':'pointer', color: '#475569', opacity: page===totalPages?0.5:1 }}>{label}</button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Pending Registration */}
      {tab === 'unregistered' && (
        <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #f1f5f9', overflow: 'hidden' }}>
          {unregistered.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>✅</div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#475569' }}>All devices registered!</div>
              <div style={{ fontSize: '14px', color: '#94a3b8', marginTop: '4px' }}>No pending ONTs found</div>
            </div>
          ) : (
            <>
              <div style={{ padding: '14px 20px', background: '#fffbeb', borderBottom: '1px solid #fef3c7', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '18px' }}>⚠️</span>
                <span style={{ fontSize: '14px', color: '#92400e', fontWeight: '600' }}>
                  {unregistered.length} ONT{unregistered.length > 1 ? 's' : ''} connected to ACS but not yet registered
                </span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                    {['Device ID', 'Manufacturer', 'Model', 'OUI', 'Last Inform', 'Action'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {unregistered.map((d, idx) => (
                    <tr key={d._id} style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '12px', color: '#475569', background: '#f1f5f9', padding: '3px 8px', borderRadius: '6px' }}>{d._deviceId._SerialNumber}</span>
                        <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '2px' }}>ID: {d._id}</div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: `${primary}10`, color: primary, padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>{d._deviceId._Manufacturer}</span>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#374151' }}>{d._deviceId._ProductClass || '—'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '12px', fontFamily: 'monospace', color: '#94a3b8' }}>{d._deviceId._OUI || '—'}</td>
                      <td style={{ padding: '14px 16px', fontSize: '12px', color: '#64748b' }}>
                        {d._lastInform ? new Date(d._lastInform).toLocaleString('en-IN') : 'N/A'}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button className="action-btn" onClick={() => handleRegisterModal(d)}
                            style={{ padding: '8px 16px', background: primary, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
                            + Register
                          </button>
                          <button className="action-btn" onClick={() => handleDeleteAcs(d._id || d.genieacs_id)}
                            style={{ padding: '8px 12px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '700' }}>
                            🗑️ Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Registration Modal */}
      {registerModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '500px', boxShadow: '0 24px 60px rgba(0,0,0,0.3)', animation: 'slideIn 0.2s ease' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>📡 Register ONT</h3>
              <button onClick={() => setRegisterModal(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', fontSize: '16px' }}>✕</button>
            </div>
            <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '12px', marginBottom: '16px', fontSize: '13px', color: '#475569' }}>
              <strong>Serial:</strong> {registerModal._deviceId._SerialNumber} | <strong>Model:</strong> {registerModal._deviceId._ProductClass || 'Unknown'}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>Customer Name *</label>
                <input style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
                  value={registerForm.customer_name} onChange={e => setRegisterForm({...registerForm, customer_name: e.target.value})} placeholder="Full name" />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>Mobile Number *</label>
                <input style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
                  value={registerForm.customer_phone} onChange={e => setRegisterForm({...registerForm, customer_phone: e.target.value})} placeholder="10-digit mobile" />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>Connection ID</label>
                <input style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
                  value={registerForm.customer_id} onChange={e => setRegisterForm({...registerForm, customer_id: e.target.value})} placeholder="Billing/PPPoE ID" />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>Area</label>
                <input style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
                  value={registerForm.area} onChange={e => setRegisterForm({...registerForm, area: e.target.value})} placeholder="Location" />
              </div>
              {subdists.length > 0 && (
                <div>
                  <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>Sub-Distribution</label>
                  <select style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
                    value={registerForm.sub_dist_id} onChange={e => { setRegisterForm({...registerForm, sub_dist_id: e.target.value, lco_id: ''}); setFilteredLcos(e.target.value ? lcos.filter(l => l.sub_dist_id === e.target.value) : lcos); }}>
                    <option value=''>-- Select Sub-Dist --</option>
                    {subdists.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                  </select>
                </div>
              )}
              {lcos.length > 0 && (
                <div>
                  <label style={{ fontSize: '11px', color: '#64748b', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px', display: 'block', marginBottom: '6px' }}>LCO</label>
                  <select style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
                    value={registerForm.lco_id} onChange={e => setRegisterForm({...registerForm, lco_id: e.target.value})}>
                    <option value=''>-- Select LCO --</option>
                    {(filteredLcos.length > 0 ? filteredLcos : lcos).map(l => <option key={l._id} value={l._id}>{l.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div style={{ marginTop: '16px', padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0', fontSize: '13px', color: '#166534' }}>
              💡 WiFi will be auto-configured: SSID = TenantCode_CustomerName, Password = Mobile Number
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
              <button onClick={handleRegisterSubmit} disabled={registerLoading}
                style={{ flex: 1, padding: '12px', background: registerLoading ? '#ccc' : primary, color: 'white', border: 'none', borderRadius: '10px', cursor: registerLoading ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px' }}>
                {registerLoading ? '⏳ Registering...' : '✅ Register & Configure'}
              </button>
              <button onClick={() => setRegisterModal(null)}
                style={{ padding: '12px 20px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Devices;
